/**
 * `useServerBackedState` (F4) — a `useState`-shaped hook whose value is
 * persisted on the Pi via `/api/userdata/:namespace` instead of the
 * browser. Mirrors the dashboard server-persistence pattern:
 *
 *   1. On mount, load the namespace blob from the agent.
 *   2. If the agent is unreachable, fall back to a localStorage mirror
 *      (so the UI still works offline / during a deploy).
 *   3. Every update writes through to the agent AND refreshes the
 *      localStorage mirror, so a later offline load has fresh data.
 *
 * The stored blob is opaque to the hook; callers own its shape and pass
 * a `defaultValue` used until the first successful load (and when the
 * namespace has never been written).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { userDataApi } from '../lib/userdata-api';

/** localStorage mirror key for a namespace's last-known value. */
export function mirrorKey(namespace: string): string {
  return `reefapp.userdata.${namespace}`;
}

export type ServerBackedSource = 'loading' | 'server' | 'local' | 'default';

export interface ServerBackedState<T> {
  readonly value: T;
  readonly setValue: (next: T) => void;
  readonly loading: boolean;
  readonly error: string | null;
  /** Where the current `value` came from (for diagnostics / tests). */
  readonly source: ServerBackedSource;
}

function readMirror<T>(namespace: string): T | undefined {
  try {
    const raw = localStorage.getItem(mirrorKey(namespace));
    if (raw === null) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function writeMirror(namespace: string, value: unknown): void {
  try {
    localStorage.setItem(mirrorKey(namespace), JSON.stringify(value));
  } catch {
    // Quota / private-mode failures are non-fatal — the server copy is
    // the source of truth; the mirror is only an offline convenience.
  }
}

export function useServerBackedState<T>(
  namespace: string,
  defaultValue: T,
): ServerBackedState<T> {
  const [value, setValueState] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<ServerBackedSource>('loading');
  // Keep the latest namespace stable for the write-through callback.
  const namespaceRef = useRef(namespace);
  namespaceRef.current = namespace;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSource('loading');
    void userDataApi.get(namespace).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        if (res.value.data !== null && res.value.data !== undefined) {
          const loaded = res.value.data as T;
          setValueState(loaded);
          writeMirror(namespace, loaded);
          setSource('server');
        } else {
          // Server reachable but namespace empty — adopt the default but
          // remember we reached the server (no error).
          setSource('default');
        }
        setError(null);
      } else {
        // Agent unreachable — fall back to the localStorage mirror.
        const mirrored = readMirror<T>(namespace);
        if (mirrored !== undefined) {
          setValueState(mirrored);
          setSource('local');
        } else {
          setSource('default');
        }
        setError(res.error);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [namespace]);

  const setValue = useCallback((next: T): void => {
    const ns = namespaceRef.current;
    setValueState(next);
    writeMirror(ns, next);
    void userDataApi.put(ns, next).then((res) => {
      if (!res.ok) setError(res.error);
      else setError(null);
    });
  }, []);

  return { value, setValue, loading, error, source };
}
